import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, Loader2 } from "lucide-react";

const PRESETS = [1000, 2500, 5000, 10000, 25000];

export function WalletTopupDialog({ open, balance, onClose }) {
  const t = useT();
  const [amount, setAmount] = useState(2500);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const effectiveCents = custom.trim() ? Math.round((parseFloat(custom) || 0) * 100) : amount;
  const valid = effectiveCents >= 500 && effectiveCents <= 1000000;

  const topup = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const origin_url = `${window.location.origin}/app/coach/ads`;
      const { data } = await api.post("/campaigns/wallet/topup", { amount_cents: effectiveCents, origin_url });
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      if (data?.mock) { toast.success(t("ads.topup_ok")); onClose(true); return; }
      onClose(true);
    } catch (e) {
      const msg = apiError(e);
      toast.error(msg.includes("billing") ? t("ads.topup_billing") : msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-md" data-testid="ads-topup-dialog">
        <DialogHeader>
          <DialogTitle>{t("ads.topup_title")}</DialogTitle>
          <DialogDescription>{t("ads.topup_sub", { balance: `€${((balance || 0) / 100).toFixed(2)}` })}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          {PRESETS.map((p) => (
            <button key={p} onClick={() => { setAmount(p); setCustom(""); }} data-testid={`ads-topup-${p}`} className={`rounded-xl border p-3 text-center font-display text-lg font-semibold ${!custom && amount === p ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground"}`}>
              €{(p / 100).toFixed(0)}
            </button>
          ))}
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("ads.topup_custom")}</label>
          <div className="flex items-center gap-2">
            <Input type="number" min="5" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={t("ads.topup_custom_ph")} data-testid="ads-topup-custom" />
            <span className="text-sm text-muted-foreground">€</span>
          </div>
        </div>

        <Button className="w-full gap-1.5 rounded-full" disabled={!valid || busy} onClick={topup} data-testid="ads-topup-confirm">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />} {t("ads.topup_confirm", { amount: `€${(effectiveCents / 100).toFixed(2)}` })}
        </Button>
        <p className="text-center text-[11px] text-muted-foreground">{t("ads.topup_note")}</p>
      </DialogContent>
    </Dialog>
  );
}
